import {MaterialWithRecipes} from '@domain/materials'
import {dataTestId} from '@hconnect/uikit'
import {Add} from '@mui/icons-material'
import {Box, Button, Stack} from '@mui/material'
import {useIsMutating} from '@tanstack/react-query'
import React from 'react'
import {useFieldArray, useFormContext} from 'react-hook-form'
import {useTranslation} from 'react-i18next'

import {useAnyPermission} from '../../../../permissions'
import {CHANGE_MATERIALS_PERMISSION_TYPES} from '../../../consts'
import {useMaterialsQuery} from '../../../hooks'
import {MaterialFormDefaultValues} from '../../materials-details/ExistingMaterialDetails/hooks'

import {getNewFormRecipe} from './helpers'
import {MaterialFormRecipe} from './MaterialFormRecipe'

import {Skeleton} from '@settings/modules/common/components/skeletons'
import {useAssetsQuery, useScheduleQuery} from '@settings/modules/common/hooks'

interface MaterialSettingsRecipesTabProps {
  material: MaterialWithRecipes
}

export const MaterialSettingsRecipesTab: React.FC<MaterialSettingsRecipesTabProps> = ({
  material
}) => {
  const {t} = useTranslation()
  const canChangeMaterials = useAnyPermission(...CHANGE_MATERIALS_PERMISSION_TYPES)
  const isMaterialSaving = !!useIsMutating({mutationKey: ['editMaterial']})

  const {data: materials} = useMaterialsQuery()
  if (!materials) {
    throw new Error('BUG: materials must be loaded before rendering recipes tab')
  }

  const {data: assets} = useAssetsQuery()
  const {data: schedule} = useScheduleQuery()

  const {control} = useFormContext<MaterialFormDefaultValues>()

  const {
    fields: formRecipes,
    append: appendRecipe,
    remove: removeRecipe
  } = useFieldArray({
    control,
    name: 'recipes',
    keyName: 'formRecipeId'
  })

  const isRecipeFormDisabled = !canChangeMaterials || isMaterialSaving

  if (!schedule || !assets) {
    return (
      <Skeleton
        height="15vh"
        sx={{
          backgroundColor: ({palette}) => palette.grey[200]
        }}
      />
    )
  }

  return (
    <Stack spacing={3} {...dataTestId('recipe_card')}>
      <Box>
        <Button
          variant="text"
          startIcon={<Add />}
          disabled={isRecipeFormDisabled}
          onClick={() => {
            appendRecipe(getNewFormRecipe(material))
          }}
          {...dataTestId('add_recipe_button')}
        >
          {t('materialsSettings.addRecipe')}
        </Button>
      </Box>
      <Stack spacing={3}>
        {formRecipes.map((formRecipe, index) => {
          return (
            <MaterialFormRecipe
              key={formRecipe.formRecipeId}
              index={index}
              formRecipe={formRecipe}
              disabled={isRecipeFormDisabled}
              materials={materials}
              assets={assets}
              schedule={schedule}
              removeRecipe={removeRecipe}
            />
          )
        })}
      </Stack>
    </Stack>
  )
}
